import React, { createContext, useContext, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';
import type { User } from '@/types/auth';

interface AuthContextType {
 user: User | null;
 isLoading: boolean;
 login: (email: string, password: string) => Promise<void>;
 oauthLogin: (user: User, token: string, refreshToken: string) => void;
 logout: () => void;
 register: (
  email: string,
  password: string,
  fullName?: string
 ) => Promise<{ needsVerification: boolean }>;
 verifyEmail: (email: string, code: string) => Promise<void>;
 resendVerificationCode: (email: string) => Promise<void>;
 forgotPassword: (email: string) => Promise<void>;
 resetPassword: (
  email: string,
  code: string,
  newPassword: string
 ) => Promise<void>;
 refreshProfile: () => Promise<void>;
 clearAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
 const { toast } = useToast();
 const {
  user,
  isLoading,
  login,
  setUser,
  logout,
  register,
  verifyEmail,
  resendVerificationCode,
  forgotPassword,
  resetPassword,
  refreshProfile,
  fetchUser,
  clearAuth,
 } = useAuthStore();

 useEffect(() => {
  fetchUser();

  // Listen for auth expiration events from lib/api.ts
  const handleAuthExpired = () => {
   toast({
    title: 'Session Expired',
    description: 'Your session has expired. Please login again.',
    variant: 'destructive',
   });
   // Clear store state and redirect will happen automatically via ProtectedRoute
   clearAuth();

   // We navigate to login with a param to show the toast there
   if (window.location.pathname !== '/login') {
    window.location.href = '/login?expired=true';
   }
  };

  window.addEventListener('auth:expired', handleAuthExpired);
  return () => window.removeEventListener('auth:expired', handleAuthExpired);
 }, [fetchUser, toast]);

 const oauthLogin = (user: User, token: string, refreshToken: string) => {
  localStorage.setItem('auth_token', token);
  localStorage.setItem('refresh_token', refreshToken);
  localStorage.setItem('user_data', JSON.stringify(user));
  setUser(user);
  toast({
   title: 'Login successful',
   description: `Welcome ${user.fullName || user.email}!`,
  });
 };

 return (
  <AuthContext.Provider
   value={{
    user,
    isLoading,
    login,
    oauthLogin,
    logout,
    register,
    verifyEmail,
    resendVerificationCode,
    forgotPassword,
    resetPassword,
    refreshProfile,
    clearAuth,
   }}
  >
   {children}
  </AuthContext.Provider>
 );
}

export function useAuth() {
 const context = useContext(AuthContext);
 if (context === undefined) {
  throw new Error('useAuth must be used within an AuthProvider');
 }
 return context;
}
